import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';

/**
 * JoinPage - Entry point for students joining a live challenge.
 *
 * Reads an optional `code` from the URL search params (e.g. from a QR code).
 * If the code is present it auto-submits; otherwise the student types the
 * access code manually.
 */
const JoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession, subscribe } = useLiveChallenge();

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false);

  const handleJoin = useCallback(
    async accessCode => {
      if (!accessCode || loading) return;

      setLoading(true);
      try {
        const response = await liveChallengesApi.join(accessCode);
        const sessionData = response?.data ?? response;

        setSession(sessionData);
        subscribe(sessionData.live_challenge_id, 'student');
        navigate(`/desafio-ao-vivo/${sessionData.live_challenge_id}/aguardando`);
      } catch (error) {
        const message = error?.response?.data?.error || error?.response?.data?.message || 'Código inválido ou desafio não encontrado.';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [loading, navigate, setSession, subscribe]
  );

  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode && !autoJoinAttempted) {
      setAutoJoinAttempted(true);
      setCode(urlCode);
      handleJoin(urlCode);
    }
  }, [searchParams, autoJoinAttempted, handleJoin]);

  const handleSubmit = e => {
    e.preventDefault();
    handleJoin(code.trim());
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bright px-4 py-12">
      <div className="w-full max-w-md">
        <header className="mb-6 flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-dropshadow-md">
            <FiZap className="h-7 w-7" />
          </div>
          <h1 className="m-0 text-2xl font-extrabold text-foreground">Desafio ao Vivo</h1>
          <p className="m-0 text-center text-sm text-neutral">Digite o código de acesso para entrar na sala.</p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-dropshadow-lg"
        >
          <label
            htmlFor="access-code"
            className="text-sm font-semibold text-foreground"
          >
            Código de acesso
          </label>
          <input
            id="access-code"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={10}
            autoFocus
            className="focus:ring-primary/20 w-full rounded-lg border-2 border-solid border-primary-lightest bg-bright p-4 text-center font-mono text-3xl font-bold uppercase tracking-[0.4em] text-primary-dark placeholder:font-normal placeholder:tracking-[0.2em] placeholder:text-neutral-light hover:border-primary-light focus:border-primary focus:bg-white focus:outline-none focus:ring-2"
          />

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            disabled={loading || !code.trim()}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-neutral-light">Peça o código ao professor ou escaneie o QR code mostrado no telão.</p>
      </div>
    </div>
  );
};

export default JoinPage;
